import React, { useEffect, useState } from "react";
import { X, UserPlus, Trash2, Check } from "lucide-react";
import { collection, query, where, getDocs, doc, updateDoc, onSnapshot, arrayUnion, arrayRemove, serverTimestamp, addDoc, deleteField } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { utentiCondivisibili } from "../utils/condivisione";

// Condivisione della commessa con colleghi della STESSA azienda (ott 2026, utils/condivisione.js):
// la vedono anche se la commessa ha membri, ricevono una notifica e collaborano alle attività
// (commenti, campi dati, sottoattività, nuove attività). Chi può condividere: vedi puoCondividereCommessa.

export default function ShareProjectModal({ projectId, projectName, onClose }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [allUsers, setAllUsers] = useState([]);
  const [sharedWith, setSharedWith] = useState([]);
  const [nomiShared, setNomiShared] = useState({});
  const [loading, setLoading] = useState(false);

  const { effectiveOrgId } = useOrg();

  useEffect(() => {
    if (!effectiveOrgId) return;
    // Carica utenti dell'organizzazione
    getDocs(query(collection(db, "users"),
      where("organizationId", "==", effectiveOrgId),
      where("enabled", "==", true)
    )).then(snap => {
      setAllUsers(utentiCondivisibili(snap.docs.map(d => ({ uid: d.id, ...d.data() })), { mioUid: currentUser.uid, orgId: effectiveOrgId }));
    });

    // Ascolta sharedWith del progetto
    const unsub = onSnapshot(doc(db, "projects", projectId), snap => {
      if (snap.exists()) { setSharedWith(snap.data().sharedWith || []); setNomiShared(snap.data().sharedWithNomi || {}); }
    });
    return unsub;
  }, [projectId, effectiveOrgId]);

  const isShared = uid => sharedWith.includes(uid);

  const toggleShare = async user => {
    setLoading(true);
    try {
      if (isShared(user.uid)) {
        await updateDoc(doc(db, "projects", projectId), { sharedWith: arrayRemove(user.uid), [`sharedWithNomi.${user.uid}`]: deleteField() });
        addToast(`Condivisione tolta a ${user.displayName}`, "info");
      } else {
        await updateDoc(doc(db, "projects", projectId), { sharedWith: arrayUnion(user.uid), [`sharedWithNomi.${user.uid}`]: user.displayName || "" });
        await addDoc(collection(db, "notifiche"), {
          userId: user.uid, orgId: effectiveOrgId, tipo: "commessa_condivisa", titolo: "Commessa condivisa con te",
          testo: `${currentUser?.displayName || "Un collega"} ha condiviso con te la commessa «${projectName || ""}»`,
          projectId, letta: false, createdAt: serverTimestamp(),
        }).catch(() => {});
        addToast(`Commessa condivisa con ${user.displayName}: riceve una notifica`, "success");
      }
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setLoading(false);
  };

  // Anche chi nel frattempo è stato disattivato resta in elenco, così si può togliere
  const sharedUsers = sharedWith.map(uid => allUsers.find(u => u.uid === uid)
    || { uid, displayName: nomiShared[uid] || "Collega", email: "utente non più attivo", disattivato: true });
  const notSharedUsers = allUsers.filter(u => !isShared(u.uid));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:500 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700 }}>Condividi commessa</h3>
            <p style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>{projectName}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>

        <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#166534" }}>
          ℹ️ Solo colleghi della tua azienda. Chi la riceve la trova nell'elenco delle commesse anche se la commessa ha membri riservati, riceve una notifica e collabora: vede tutte le attività, commenta, compila i campi dati, aggiunge sottoattività e nuove attività. Nome, stato e scadenze delle attività restano a chi le segue. Per una singola attività usa invece «👥 Condividi» nel pannello dell'attività.
        </div>

        {sharedUsers.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:8 }}>
              Hanno accesso ({sharedUsers.length})
            </div>
            {sharedUsers.map(u => (
              <div key={u.uid} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, marginBottom:6 }}>
                <div className="avatar" style={{ width:32, height:32, fontSize:13 }}>{u.displayName?.[0]?.toUpperCase()}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>{u.displayName}</div>
                  <div style={{ fontSize:11, color:"#9ca3af" }}>{u.disattivato ? u.email : `${u.email} · ${u.department || "Nessun reparto"}`}</div>
                </div>
                <Check size={14} color="#059669"/>
                <button onClick={() => toggleShare(u)} disabled={loading} className="btn-icon" title="Togli la condivisione" style={{ color:"#dc2626" }}>
                  <Trash2 size={14}/>
                </button>
              </div>
            ))}
          </div>
        )}

        <div>
          <div style={{ fontSize:12, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:8 }}>
            Colleghi ({notSharedUsers.length} disponibili)
          </div>
          {notSharedUsers.length === 0 ? (
            <div style={{ fontSize:13, color:"#9ca3af", padding:12, textAlign:"center", background:"#f9fafb", borderRadius:8 }}>
              Già condivisa con tutti i colleghi
            </div>
          ) : notSharedUsers.map(u => (
            <div key={u.uid} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:6 }}>
              <div className="avatar" style={{ width:32, height:32, fontSize:13, background:"#6b7280" }}>{u.displayName?.[0]?.toUpperCase()}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600 }}>{u.displayName}</div>
                <div style={{ fontSize:11, color:"#9ca3af" }}>{u.email} · {u.department || "Nessun reparto"}</div>
              </div>
              <button onClick={() => toggleShare(u)} disabled={loading} className="btn btn-secondary btn-sm">
                <UserPlus size={13}/> Condividi
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
          <button className="btn btn-primary" onClick={onClose}>Fatto</button>
        </div>
      </div>
    </div>
  );
}
